'use client';

import ArticlePagination from '@/components/articlePagination/ArticlePagination';
import Breadcrumbs from '@/components/breadcrumbs/Breadcrumbs';
import HeadlineField from '@/components/customFields/HeadlineField';
import { StoryblokImage } from '@/components/customFields/StoryblokImage';
import { type GenericBlok, HeadlineTag } from '@/components/customFields/types';
import { Divider } from '@/components/divider';
import { RichText } from '@/components/richText/RichText';
import classes from '@/storyblok/PageMagAuthor/PageMagAuthor.module.scss';
import type { PageMagAuthorStoryblok } from '@/types/component-types-sb';
import type { ISbStoryData } from '@storyblok/react/rsc';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

export const ITEMS_PER_PAGE = 9;

export type PageMagAuthor = {
  articles: [];
  blok: PageMagAuthorStoryblok;
  page: number;
  pages: number;
  breadcrumbs: ISbStoryData<GenericBlok>[];
  slug: string;
};

export const PageMagAuthor: React.FC<PageMagAuthor> = (props) => {
  const { t } = useTranslation();

  return (
    <div data-testid="magazine-author-page" className={classNames(classes.magazineAuthor, 'grid-wrapper')}>
      <header>
        {props.page === 1 && (
          <div className={classes.profileImage}>
            <StoryblokImage
              image={props.blok.image}
              alt={props.blok.imageAltTag}
              width={{
                pxMobile: 56,
                pxTablet: 137,
                pxDesktop: 137,
              }}
            />
          </div>
        )}
        <div className={classes.pageTitle}>
          <HeadlineField data-testid="magazine-author-name" tag={HeadlineTag.H1} className={classes.title}>
            {props.blok.first_name} {props.blok.last_name} {props.page > 1 && `/ ${t('pagination.page')} ${props.page}`}
          </HeadlineField>
          <div className={classes.subTitle}>{props.blok.title}</div>
        </div>
      </header>

      {props.page === 1 && (
        <div className={classes.intro}>
          <HeadlineField tag={HeadlineTag.H2} className={classes.introHeadline}>
            {props.blok.pronouns === 'f' && t('magazineAuthor.headlineF')}
            {props.blok.pronouns === 'm' && t('magazineAuthor.headlineM')}
          </HeadlineField>
          <RichText text={props.blok.description} className={classes.introDescription} />
        </div>
      )}

      <Divider />

      <Breadcrumbs linkLastElementInBreadcrumb={true} className={classes.breadcrumbs} stories={props.breadcrumbs} />

      <HeadlineField tag={HeadlineTag.H2} style={HeadlineTag.H3}>
        {t('magazineAuthor.headlineArticles').replace(
          '%%name%%',
          props.blok.first_name?.endsWith('s') ? `${props.blok.first_name}’` : `${props.blok.first_name}s`
        )}
      </HeadlineField>

      <ArticlePagination articles={props.articles} baseUrl={props.slug} page={props.page} count={props.pages} />
    </div>
  );
};
export default PageMagAuthor;
